/**
 * The list contract shared by the API and every list screen. It is also the
 * URL format of list pages, so a filtered view can be bookmarked or sent in
 * LINE as a link:
 *
 *   ?page=2&pageSize=50&sort=-pickupDate&q=ลาดกระบัง
 *   &f.status=pending,assigned          (any of)
 *   &f.pickupDate=2026-10-01..2026-10-31 (range, either side optional)
 */

export type FilterValue = { op: 'in'; values: string[] } | { op: 'range'; from?: string; to?: string }

export interface ListParams {
  page: number
  pageSize: number
  sort?: string
  q?: string
  filters: Record<string, FilterValue>
  /** One's own records only (the resource's `mine` fields), within what one may read: `?mine=1` (Thananan OS) */
  mine?: boolean
}

/** Labels of referenced records, keyed by resource key then id. */
export type RefMap = Record<string, Record<string, string>>

export interface ListResult<T> {
  items: T[]
  total: number
  page: number
  pageSize: number
  refs: RefMap
  /** Counts per value for fields requested with `facets=status,…` */
  facets?: Record<string, Record<string, number>>
}

export const DEFAULT_PAGE_SIZE = 25
/** Upper bound for one request, the largest a list shows a page; exports page through larger sets */
export const MAX_PAGE_SIZE = 10_000
/** The rows a list's page may show, up to a whole year of bills to look over or tick at once */
export const PAGE_SIZES = [25, 50, 100, 200, 1000, 5000, MAX_PAGE_SIZE] as const

export function encodeFilter(value: FilterValue): string {
  if (value.op === 'range') return `${value.from ?? ''}..${value.to ?? ''}`
  return value.values.join(',')
}

export function decodeFilter(raw: string): FilterValue | undefined {
  if (raw.includes('..')) {
    const [from, to] = raw.split('..')
    if (!from && !to) return undefined
    return { op: 'range', from: from || undefined, to: to || undefined }
  }
  const values = raw
    .split(',')
    .map((v) => v.trim())
    .filter(Boolean)
  return values.length ? { op: 'in', values } : undefined
}

function toInt(raw: string | null | undefined, fallback: number, max = Number.MAX_SAFE_INTEGER): number {
  const n = Number.parseInt(raw ?? '', 10)
  if (!Number.isFinite(n) || n < 1) return fallback
  return Math.min(n, max)
}

export function parseListParams(input: URLSearchParams | Record<string, string | undefined>): ListParams {
  const get = (k: string) => (input instanceof URLSearchParams ? input.get(k) : input[k]) ?? undefined
  const entries = input instanceof URLSearchParams ? [...input.entries()] : Object.entries(input)
  const filters: Record<string, FilterValue> = {}
  for (const [k, v] of entries) {
    if (!k.startsWith('f.') || v == null) continue
    const decoded = decodeFilter(v)
    if (decoded) filters[k.slice(2)] = decoded
  }
  return {
    page: toInt(get('page'), 1),
    pageSize: toInt(get('pageSize'), DEFAULT_PAGE_SIZE, MAX_PAGE_SIZE),
    sort: get('sort') || undefined,
    q: get('q')?.trim() || undefined,
    filters,
    mine: get('mine') === '1' || undefined,
  }
}

export function toSearchParams(params: Partial<ListParams>): URLSearchParams {
  const sp = new URLSearchParams()
  if (params.page && params.page > 1) sp.set('page', String(params.page))
  if (params.pageSize && params.pageSize !== DEFAULT_PAGE_SIZE) sp.set('pageSize', String(params.pageSize))
  if (params.sort) sp.set('sort', params.sort)
  if (params.q) sp.set('q', params.q)
  if (params.mine) sp.set('mine', '1')
  for (const [field, value] of Object.entries(params.filters ?? {})) sp.set(`f.${field}`, encodeFilter(value))
  return sp
}

export interface SortKey {
  field: string
  desc: boolean
}

export function parseSort(sort: string | undefined): SortKey[] {
  if (!sort) return []
  return sort
    .split(',')
    .map((s) => s.trim())
    .filter(Boolean)
    .map((s) => (s.startsWith('-') ? { field: s.slice(1), desc: true } : { field: s, desc: false }))
}
